import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Message } from 'primeng/api';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SubstanceAbuseEvaluation } from '../../compliance/models/substance-abuse-evaluation.model';
import { AddCounselorReportingRequest } from '../../driverrecord/counselor-reports/models/add-counselor-reporting-request.model';
import { EventHistory, EventHistoryQuery } from '../../driverrecord/event-history.model';
import { ComplianceMonitoringVehicle } from '../../driverrecord/models/compliance-monitoring-vehicle.model';
import { ManualComplianceMonitoringDetail } from '../../driverrecord/models/manual-compliance-monitoring-detail.model';
import { BaseApiService } from '../../shared/base-service/base-api.service';
import { ProhibitionIncidentDetailModel } from '../models/prohibition-incident-detail.model';
import { ProhibitionItemKey } from '../models/prohibition-item-key.model';
import { TssAssignmentDetail } from '../models/tss-assignment-detail';

@Injectable()
export class ProhibitionService extends BaseApiService {
    public growlMessages: Message[] = [];

    public evaluateProhibition(incidentId: number) {
        return this.get(`Prohibition/Evaluate/${incidentId}`);
    }

    public reevaluateProhibition(incidentId: number) {
        return this.get(`Prohibition/Reevaluate/${incidentId}`);
    }

    public getProhibitionByCustomer(customerNumberId: number) {
        return this.get(`Prohibition/Customer/${customerNumberId}`).pipe(map(response => response.result));
    }

    public getProhibitionByItemKeys = (itemKeys: ProhibitionItemKey[]): Observable<{ result: ProhibitionIncidentDetailModel[] }> => this.post('prohibition/management/query', itemKeys);

    public getProhibitionStatusTypes = (): Observable<any> => this.get('Prohibition/StatusTypes');

    public getDrivingRecordEvents(customerNumberId: number) {
        return this.get(`Prohibition/DrivingRecord/events/${customerNumberId}`);
    }

    public getVehicleRecordEvents(vehicleId: string) {
        return this.get(`Prohibition/VehicleRecord/events/${vehicleId}`);
    }

    public getComplianceHistory(customerNumberId: number) {
        return this.get(`ComplianceMonitoring/history/${customerNumberId}`);
    }

    public getComplianceMonitoringStatusType() {
        return this.get('ComplianceMonitoring/types');
    }

    public getComplianceMonitoringStatusNewType() {
        return this.get('ComplianceMonitoring/new/types');
    }

    public getComplianceMonitoringVehicleStatusType() {
        return this.get('ComplianceMonitoring/VehicleStatusTypes');
    }

    public getComplianceMonitoringType() {
        return this.get('ComplianceMonitoring/MonitoringTypes');
    }

    public getCustomerMonitoringVehicles(customerNumberId: number) {
        return this.get(`ComplianceMonitoring/CustomerVehicles/${customerNumberId}`);
    }

    public saveManualComplianceMonitoring(data: ManualComplianceMonitoringDetail) {
        return this.post('ComplianceMonitoring/ComplianceMonitoring', data, this.nullReplacer);
    }

    public saveManualComplianceMonitoringExtension(data: ManualComplianceMonitoringDetail) {
        return this.post('ComplianceMonitoring/extension', data, this.nullReplacer);
    }

    public saveComplianceMonitoringVehicle(data: ComplianceMonitoringVehicle) {
        return this.post('ComplianceMonitoring/ComplianceMonitoringVehicle', data, this.nullReplacer);
    }

    public deferComplianceMonitoring(complianceMonitoringId: number) {
        return this.post(`ComplianceMonitoring/defer/${complianceMonitoringId}`, null);
    }

    public updateComplianceMonitoringStatus(complianceMonitoringId: number, complianceMonitoringStatusTypeId: number) {
        return this.post(`ComplianceMonitoring/update/status/${complianceMonitoringId}/${complianceMonitoringStatusTypeId}`, null);
    }

    public getComplianceMonitoringWithNoExtensionsForCustomer(customerNumberId) {
        return this.get(`ComplianceMonitoring/eligible/${customerNumberId}`);
    }
    public getDrivingRecordCredentials(customerNumberId: number, businessTransactionNumberId: number) {
        if (null == businessTransactionNumberId) {
            return this.get(`Prohibition/DrivingRecord/credentials/${customerNumberId}`);
        }

        return this.get(`Prohibition/DrivingRecord/credentials/${customerNumberId}/${businessTransactionNumberId}`);
    }

    public getCounselorReportingProhibitionCompliances(customerNumberId) {
        return this.get(`Pace/dui/counselor/CounselorReporting/ProhibitionCompliances/${customerNumberId}`);
    }

    public addCounselorReporting(addCounselorReportingRequest: AddCounselorReportingRequest) {
        return this.post('Pace/dui/counselor/CounselorReporting', addCounselorReportingRequest);
    }

    public deleteCounselorReporting(counselorReportingId: number) {
        return this.delete(`Pace/dui/counselor/CounselorReporting/${counselorReportingId}`).pipe(map(response => response.result));
    }

    public getProhibitionDetailsForType(prohibitionTypeId: number, customerNumberId: number) {
        return this.get(`/Prohibition/Type/${prohibitionTypeId}/customer/${customerNumberId}`);
    }

    public getProhibitionItemDetailsForKeyType(prohibitionTypeId: number, prohibitionItemKeyTypeId: number, prohibitionItemKeyId: number) {
        return this.get(`Prohibition/Type/${prohibitionTypeId}/entity/${prohibitionItemKeyTypeId}/${prohibitionItemKeyId}`);
    }

    public getLookups() {
        return this.get('Prohibition/Lookups');
    }

    public getProhibitionTypeStatusTypeReasonTypeMaps(prohibitionTypeId: number) {
        return this.get(`Prohibition/ProhibitionTypeStatusTypeReasonTypeMap/${prohibitionTypeId}`);
    }

    public getProhibitionType(prohibitionTypeId: number) {
        return this.get(`Prohibition/Type/${prohibitionTypeId}`);
    }

    public getProhibitionsByVehicle(vehicleId: number) {
        return this.get(`Prohibition/Vehicle/${vehicleId}`).pipe(map(response => response.result));
    }

    public getProhibitionDetails(prohibitionId: number) {
        return this.get(`Prohibition/${prohibitionId}`);
    }

    public getAAMVAProhibitionDetails(prohibitionId: number) {
        return this.get(`Prohibition/AAMVA/${prohibitionId}`);
    }

    public saveProhibition(prohibition) {
        return this.post('Prohibition', prohibition, this.replacer).pipe(
            map(response => response.result),
            catchError((error) => this.handleError(error)));
    }
    public ValidateProhibitionType(prohibitionTypeId, effectiveDate) {
        return this.get(`Prohibition/validate/prohibitiontype/${prohibitionTypeId}/${effectiveDate}`);
    }

    public reportRooswForProhibition(prohibitionId) {
        return this.post(`Prohibition/reportROOSWForProhibition/${prohibitionId}`, null);
    }

    public deleteProhibition(prohibitionId: number) {
        return this.delete(`Prohibition/${prohibitionId}`).pipe(map(response => response.result));
    }

    public getComplianceTypes() {
        return this.get('Compliance/Type');
    }
    public getComplianceRequirementTypes() {
        return this.get('Compliance/RequirementType');
    }

    public getWithdrawalHistory(prohibitionId: number) {
        return this.get(`Prohibition/WithdrawalHistory/${prohibitionId}`);
    }

    public getActiveComplianceCount(id: number) {
        return this.get(`Compliance/active/${id}`);
    }

    public getTssAssignmentDetail(id: number) {
        return this.get(`Compliance/active/${id}`);
    }

    public saveTssAssignmentDetail(model: TssAssignmentDetail) {
        return this.post('Prohibition/TSS/Detail', model, this.replacer);
    }

    public saveTssAssignmentDetailWithCompliance(model: any) {
        return this.post('Prohibition/TSS/Detail/Compliance', model, this.replacer);
    }

    public saveSubstanceAbuseEvaluation(model: SubstanceAbuseEvaluation) {
        return this.post('Compliance/SubstanceAbuseEvaluation', model, this.replacer);
    }

    public getTssAssignementDetail(id: number) {
        return this.get(`Prohibition/TSS/${id}`);
    }
    public getTssAssignmentDetailProhibitionCompliance(id: number) {
        return this.get(`Prohibition/TSS/Prohibition/${id}`);
    }

    public getCounselorReports(customerNumberId: number) {
        return this.get(`Pace/dui/counselor/Customer/${customerNumberId}`);
    }

    public getTssAssignmentProhibitionCompliance(id: number) {
        return this.get(`Prohibition/TSS/Prohibition/Assessment/${id}`);
    }

    public getEventHistory(customerNumberId: number, vehicleId: number) {
        return this.get(`Prohibition/DrivingRecord/EventHistory/${customerNumberId}/${vehicleId}`);
    }

    public queryEventHistory(query: EventHistoryQuery): Observable<{ result: EventHistory }> {
        return this.post('Prohibition/DrivingRecord/EventHistory/query', query);
    }

    public queryProhibitions(itemKeys: ProhibitionItemKey[], skip: number = 0, take: number = 10) {
        return this.post(`prohibition/query?skip=${skip}&take=${take}`, itemKeys);
    }

    public getIncidentConvictionMonitoringsForCustomer(customerNumberId: number) {
        return this.get(`ComplianceMonitoring/IncidentConvictionMonitoring/${customerNumberId}`);
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error) {
            return observableThrowError(error.error);
        } else {
            return observableThrowError('Server error');
        }
    }

    private replacer(key, value) {
        if (value === '') {
            return undefined;
        }

        return value;
    }

    private nullReplacer(key, value) {
        if (value == null) {
            return undefined;
        }

        return value;
    }
}
